import { useQueryClient } from 'react-query';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../stores/store';
import { useDeleteProjectEstimateAssemblyMutation } from './useCreateProjectEstimateAssemblyMutation';
import { useDeleteContractChangeOrderAssemblyMutation } from './useCreateContractChangeOrderAssemblyMutation';
import { useSyncInvalidation } from './useSyncConnector';
import { TakeoffAssembly } from '../types/AssemblyItemType';
import { useAssemblyStore } from '../stores/AssemblyStore';

export const useDeleteTakeoffAssembly = () => {
  const queryClient = useQueryClient();
  const { invalidateSync } = useSyncInvalidation();

  const { selectedEstimate, selectedChangeOrder } = useStore(useUnityBuildStore);

  const { setCurrentTakeoffAssembly } = useStore(useAssemblyStore);

  const { mutateAsync: deleteEstimateAssembly } = useDeleteProjectEstimateAssemblyMutation();
  const { mutateAsync: deleteChangeOrderAssembly } = useDeleteContractChangeOrderAssemblyMutation();

  /**
   * Delete an assembly with optimistic updates to prevent UI flicker
   */
  const deleteAssembly = async (assembly: TakeoffAssembly) => {
    if (!assembly || !assembly.assemblyid) {
      console.error('Invalid assembly provided for deletion');
      return;
    }

    // Define query keys for better clarity and reuse
    const baseQueryKey = ['countedAssemblies'];
    const estimateQueryKey = selectedEstimate
      ? ['countedAssemblies', selectedEstimate.estimateid]
      : null;
    const changeOrderQueryKey = selectedChangeOrder
      ? ['countedAssemblies', null, selectedChangeOrder.changeorderid]
      : null;

    // Get the specific query key for this operation
    const specificQueryKey = estimateQueryKey || changeOrderQueryKey || baseQueryKey;

    // Cancel ongoing queries for the specific key to prevent race conditions
    await queryClient.cancelQueries(specificQueryKey);

    // Capture the previous state for rollback if needed
    const previousData = queryClient.getQueryData(specificQueryKey);

    // Function to update the query cache with the assembly removed
    const updateQueryCache = (key: unknown[], isSpecificKey = false) => {
      queryClient.setQueryData(key, (oldAssemblies: any[] | undefined) => {
        // Ensure oldAssemblies is not undefined
        const old = oldAssemblies || [];

        // For updates on the specific key, make a deeper copy to avoid reference issues
        const assembliesToUpdate = isSpecificKey ? JSON.parse(JSON.stringify(old)) : old;

        // Filter out the assembly being deleted and empty containers
        return assembliesToUpdate
          .map((asm: any) => ({
            ...asm,
            assembliesList: asm.assembliesList.filter(
              (a: TakeoffAssembly) => a.assemblyid !== assembly.assemblyid
            ),
          }))
          .filter((asm: any) => asm.assembliesList.length > 0);
      });
    };

    // Update the cache optimistically for all relevant query keys
    updateQueryCache(specificQueryKey, true);
    updateQueryCache(baseQueryKey, false);

    setCurrentTakeoffAssembly(null);

    try {
      // Execute the actual server-side deletion
      if (selectedEstimate) {
        await deleteEstimateAssembly(assembly.assemblyid);

        if (estimateQueryKey) {
          invalidateSync(estimateQueryKey);
        }
      } else if (selectedChangeOrder) {
        await deleteChangeOrderAssembly(assembly.assemblyid);

        if (changeOrderQueryKey) {
          invalidateSync(changeOrderQueryKey);
        }
      }

      // Notify sync system without immediate invalidation
      invalidateSync(baseQueryKey);

      // Schedule a background refresh after a delay to ensure UI stability
      setTimeout(() => {
        // This will refresh the data in the background without clearing the cache first
        queryClient.refetchQueries(specificQueryKey, { exact: true });
        queryClient.refetchQueries(baseQueryKey, { exact: false });
      }, 500);
    } catch (error) {
      // Rollback optimistic updates if the server request fails
      if (previousData) {
        queryClient.setQueryData(specificQueryKey, previousData);
        queryClient.setQueryData(baseQueryKey, previousData);
      }

      // After rollback, we can safely invalidate to ensure data consistency
      queryClient.invalidateQueries(baseQueryKey);
      throw error;
    }
  };

  return { deleteAssembly };
};
